/**
 * MasterDataPage — card-hub landing cho Master Data (style mượn từ Factory
 * Data hub `/<code>/data`).
 *
 * `/master-data` render 4 block ĐỒNG CẤP, mỗi block là 1 "page" trong Access
 * (frmMdMainModFuture / frmMdMainModItemsCPList / …ItemsValuation / …ItemsOther).
 * Card trong mỗi block = các option của combobox `cbPreferencesList` (Access
 * "Go to Page:") — trích 1:1 từ RowSource trong tài liệu Access:
 *   - Futures Control        (nav buttons): Calendar | Month | Holiday
 *   - CP List                : Vendor | CP Bank | License | Service Type |
 *                              Service | Doc Dispatch
 *   - Items and Valuation    : General Info | Valuation | Updates | Type of
 *                              Inspection | Dunnage | Shipping Doc | Claim
 *                              Condition | Type of Claim | List Warehouse |
 *                              Processing Type
 *   - Other List             : Contract Type | Certification | Bank | Delivery
 *                              Term | Package | Delivery Place | TLC Bank |
 *                              Consignee | CVBB Type | Export Terminal | Vessel
 *                              Name | Logistic Cost
 *
 * Bấm card → điều hướng tới leaf route (hiện parked → ComingSoonPage). Không
 * còn inner Tabs / sidebar sub-navigation: hub là điểm điều hướng duy nhất.
 *
 * URL grammar (phẳng theo 4 block đồng cấp):
 *   /master-data                                → hub landing
 *   /master-data/<block>/<slug>                 → leaf
 *   block ∈ futures-control | cp-list | items-valuation | other
 */
import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventNoteIcon from '@mui/icons-material/EventNote'
import EventIcon from '@mui/icons-material/Event'
import GroupsIcon from '@mui/icons-material/Groups'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BadgeIcon from '@mui/icons-material/Badge'
import CategoryIcon from '@mui/icons-material/Category'
import HandymanIcon from '@mui/icons-material/Handyman'
import DescriptionIcon from '@mui/icons-material/Description'
import InfoIcon from '@mui/icons-material/Info'
import PaidIcon from '@mui/icons-material/Paid'
import UpdateIcon from '@mui/icons-material/Update'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import GavelIcon from '@mui/icons-material/Gavel'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import SettingsIcon from '@mui/icons-material/Settings'
import ArticleIcon from '@mui/icons-material/Article'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import AssignmentIcon from '@mui/icons-material/Assignment'
import InventoryIcon from '@mui/icons-material/Inventory'
import PlaceIcon from '@mui/icons-material/Place'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PersonIcon from '@mui/icons-material/Person'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import SailingIcon from '@mui/icons-material/Sailing'
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat'
import LocalAtmIcon from '@mui/icons-material/LocalAtm'
import { useLocation, useNavigate } from 'react-router-dom'

import PageHeader from '../components/PageHeader'
import { CARD_CONTENT_COMPACT, CARD_GRID_4UP } from '../theme/responsive'
import ComingSoonPage from './ComingSoonPage'
import { getUnbuild } from '../config/pageBuildStatus'
import MasterDataCalendarView from './masterData/MasterDataCalendarView'
import MasterDataMonthView from './masterData/MasterDataMonthView'
import MasterDataHolidayView from './masterData/MasterDataHolidayView'

// Leaf path → page thật (Factory Data workspace style). Card chưa build không
// có entry ở đây → rơi xuống ComingSoon. Nút "Datasets" trong page back về hub.
const MASTER_DATA_PAGES: Record<string, React.ComponentType<{ onBackToHub?: () => void }>> = {
  '/master-data/calendar': MasterDataCalendarView,
  '/master-data/month': MasterDataMonthView,
  '/master-data/holiday': MasterDataHolidayView,
}

type HubCardSpec = {
  slug: string
  label: string
  path: string
  Icon: React.ComponentType<{ fontSize?: 'small' | 'medium' }>
}
type HubGroupSpec = { key: string; title: string; color: string; cards: HubCardSpec[] }

// Leaf URL phẳng tuyệt đối: /master-data/<slug> (bỏ segment block). 31 slug
// đều duy nhất cross-block nên không đụng nhau. `block` giữ ở call site cho
// gom nhóm hub nhưng KHÔNG vào URL.
const fcPath = (slug: string) => `/master-data/${slug}`
const itemsPath = (_block: string, slug: string) => `/master-data/${slug}`

// 4 block đồng cấp. Order + label bám 1:1 RowSource `cbPreferencesList` Access.
const MASTER_DATA_HUB: HubGroupSpec[] = [
  {
    key: 'futures-control',
    title: 'FUTURES CONTROL',
    color: '#145a59',
    cards: [
      { slug: 'calendar', label: 'Calendar', path: fcPath('calendar'), Icon: CalendarMonthIcon },
      { slug: 'month', label: 'Month', path: fcPath('month'), Icon: EventNoteIcon },
      { slug: 'holiday', label: 'Holiday', path: fcPath('holiday'), Icon: EventIcon },
    ],
  },
  {
    key: 'cp-list',
    title: 'CP LIST',
    color: '#5b8f3a',
    cards: [
      { slug: 'vendor', label: 'Vendor', path: itemsPath('cp-list', 'vendor'), Icon: GroupsIcon },
      { slug: 'cp-bank', label: 'CP Bank', path: itemsPath('cp-list', 'cp-bank'), Icon: AccountBalanceIcon },
      { slug: 'license', label: 'License', path: itemsPath('cp-list', 'license'), Icon: BadgeIcon },
      { slug: 'service-type', label: 'Service Type', path: itemsPath('cp-list', 'service-type'), Icon: CategoryIcon },
      { slug: 'service', label: 'Service', path: itemsPath('cp-list', 'service'), Icon: HandymanIcon },
      { slug: 'doc-dispatch', label: 'Doc Dispatch', path: itemsPath('cp-list', 'doc-dispatch'), Icon: DescriptionIcon },
    ],
  },
  {
    key: 'items-valuation',
    title: 'ITEMS AND VALUATION',
    color: '#4a3d8f',
    cards: [
      { slug: 'general-info', label: 'General Info', path: itemsPath('items-valuation', 'general-info'), Icon: InfoIcon },
      { slug: 'valuation', label: 'Valuation', path: itemsPath('items-valuation', 'valuation'), Icon: PaidIcon },
      { slug: 'updates', label: 'Updates', path: itemsPath('items-valuation', 'updates'), Icon: UpdateIcon },
      { slug: 'type-of-inspection', label: 'Type of Inspection', path: itemsPath('items-valuation', 'type-of-inspection'), Icon: FactCheckIcon },
      { slug: 'dunnage', label: 'Dunnage', path: itemsPath('items-valuation', 'dunnage'), Icon: Inventory2Icon },
      { slug: 'shipping-doc', label: 'Shipping Doc', path: itemsPath('items-valuation', 'shipping-doc'), Icon: LocalShippingIcon },
      { slug: 'claim-condition', label: 'Claim Condition', path: itemsPath('items-valuation', 'claim-condition'), Icon: GavelIcon },
      { slug: 'type-of-claim', label: 'Type of Claim', path: itemsPath('items-valuation', 'type-of-claim'), Icon: ReportProblemIcon },
      { slug: 'list-warehouse', label: 'List Warehouse', path: itemsPath('items-valuation', 'list-warehouse'), Icon: WarehouseIcon },
      { slug: 'processing-type', label: 'Processing Type', path: itemsPath('items-valuation', 'processing-type'), Icon: SettingsIcon },
    ],
  },
  {
    key: 'other',
    title: 'OTHER LIST',
    color: '#a14b2a',
    cards: [
      { slug: 'contract-type', label: 'Contract Type', path: itemsPath('other', 'contract-type'), Icon: ArticleIcon },
      { slug: 'certification', label: 'Certification', path: itemsPath('other', 'certification'), Icon: WorkspacePremiumIcon },
      { slug: 'bank', label: 'Bank', path: itemsPath('other', 'bank'), Icon: AccountBalanceIcon },
      { slug: 'delivery-term', label: 'Delivery Term', path: itemsPath('other', 'delivery-term'), Icon: AssignmentIcon },
      { slug: 'package', label: 'Package', path: itemsPath('other', 'package'), Icon: InventoryIcon },
      { slug: 'delivery-place', label: 'Delivery Place', path: itemsPath('other', 'delivery-place'), Icon: PlaceIcon },
      { slug: 'tlc-bank', label: 'TLC Bank', path: itemsPath('other', 'tlc-bank'), Icon: AccountBalanceWalletIcon },
      { slug: 'consignee', label: 'Consignee', path: itemsPath('other', 'consignee'), Icon: PersonIcon },
      { slug: 'cvbb-type', label: 'CVBB Type', path: itemsPath('other', 'cvbb-type'), Icon: ConfirmationNumberIcon },
      { slug: 'export-terminal', label: 'Export Terminal', path: itemsPath('other', 'export-terminal'), Icon: SailingIcon },
      { slug: 'vessel-name', label: 'Vessel Name', path: itemsPath('other', 'vessel-name'), Icon: DirectionsBoatIcon },
      { slug: 'logistic-cost', label: 'Logistic Cost', path: itemsPath('other', 'logistic-cost'), Icon: LocalAtmIcon },
    ],
  },
]

// Lookup phẳng: path → { label } để render leaf ComingSoon đúng tiêu đề.
const CARD_BY_PATH = new Map<string, HubCardSpec>(
  MASTER_DATA_HUB.flatMap((g) => g.cards.map((c) => [c.path, c] as const)),
)

function MasterDataHubCard({ card, color, onClick }: { card: HubCardSpec; color: string; onClick: () => void }) {
  const { Icon } = card
  return (
    <Card
      onClick={onClick}
      sx={{
        height: 48,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,249,244,0.86))',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          borderColor: alpha(color, 0.5),
        },
      }}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, height: '100%', display: 'flex', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.25,
              display: 'grid',
              placeItems: 'center',
              color,
              backgroundColor: alpha(color, 0.12),
              border: `1px solid ${alpha(color, 0.18)}`,
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {card.label}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}

function MasterDataHub({ onOpen }: { onOpen: (path: string) => void }) {
  return (
    <Box sx={{ flex: 1, p: 0.5 }}>
      <Stack spacing={1}>
        {MASTER_DATA_HUB.map((group) => (
          <Box key={group.key}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                mb: 0.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderLeft: 3,
                borderColor: 'primary.main',
                borderRadius: 0.5,
              }}
            >
              <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: 0.04, lineHeight: 1.2 }}>
                {group.title}
              </Typography>
            </Box>
            <Box sx={CARD_GRID_4UP}>
              {group.cards.map((card) => (
                <Box key={card.slug}>
                  <MasterDataHubCard card={card} color={group.color} onClick={() => onOpen(card.path)} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default function MasterDataPage() {
  const location = useLocation()
  const navigate = useNavigate()

  // Page thật đã build → render workspace (tự có back-to-hub + title pill).
  const leaf = CARD_BY_PATH.get(location.pathname)

  // Page parked (to-test/to-build, không revealed) → bỏ qua Built, rơi xuống
  // nhánh leaf → ComingSoonPage bên dưới.
  const parked = !!getUnbuild(location.pathname)

  const Built = MASTER_DATA_PAGES[location.pathname]
  if (Built && !parked) {
    // GIỮ header block chung "Master Data" (như hub + các leaf khác), rồi view
    // fill phần còn lại. height:'100%' (KHÔNG minHeight) để chuỗi flex có chiều
    // cao xác định → TableVirtuoso không collapse về 0. Xem [[app-shell-scroll]].
    return (
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeader title={`Master Data — ${leaf?.label ?? ''}`} />
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Built onBackToHub={() => navigate('/master-data')} />
        </Box>
      </Box>
    )
  }

  // Leaf: path khớp 1 card → render ComingSoon với đúng tiêu đề card.
  if (leaf) {
    return (
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <PageHeader title={`Master Data — ${leaf.label}`} />
        <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={CARD_CONTENT_COMPACT}>
            <ComingSoonPage embedded title={leaf.label} status={getUnbuild(location.pathname)?.status} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  // Mặc định (`/master-data` hoặc path chưa khớp) → hub landing.
  return (
    <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader title="Master Data" />
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          <MasterDataHub onOpen={(path) => navigate(path)} />
        </CardContent>
      </Card>
    </Box>
  )
}
